/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { Size } from "./types";

export const APPBAR_HEIGHT: Size = { xs: 80, sm: 80, md: 116, lg: 128 };

export const CONTENT_LAYOUT_WIDTH = 1280;

export const PAGE_PADDING_SIDE: Size = { xs: 16, sm: 32, md: 48, lg: 56, xl: 96 };
export const PAGE_PADDING_TOP: Size = { xs: 16, sm: 8, md: 56, lg: 72, xl: 100 };

export const BOTTOM_NAVIGATION_HEIGHT = 64;

export const CATEGORY_DEFAULT_OPTION = "All available categories";
export enum SortingOptions {
  MostRecent = "Most Recent",
  MostOrdered = "Most Ordered",
  PriceDescending = "Price (High to Low)",
  PriceAscending = "Price (Low to High)",
  Alphabetical = "Alphabetical (A-Z)",
  BestMatch = "Best Match",
}

export const SORTING_OPTIONS = Object.values(SortingOptions).filter((v) => isNaN(Number(v)));

export const PAGINATION_DATA_LIMIT = 25;

export const URLS = {
  farmlandsPRO: "https://www.farmlandspro.co.nz",
  shopOnline: "https://shop.farmlands.co.nz",
  findStore: "https://www.farmlands.co.nz/find-a-store",
  findOutMore: "https://www.farmlands.co.nz/join/shareholder",
  termsAndConditions: "https://www.farmlands.co.nz/terms-and-conditions#website-and-app",
  fulfilmentFAQs: "https://shop.farmlands.co.nz/farmlandsprofaqs",
  signUpNow: "https://my.1centre.com/websitebutton/78f591f9-67aa-4057-a0c9-7297ae365a4b/start",
  imageBase:
    "https://images-ap-prod.cms.commerce.dynamics.com/cms/api/qwvspcbgds/imageFileData/search?fileName=/",
  becomeShareHolder: "https://www.farmlands.co.nz/join/shareholder",
  salesOrdersTermsAndConditions:
    "https://www.farmlands.co.nz/farmlands_terms_and_conditions/sales-orders-terms.html",
};

export const CONTACT_CARD_TITLE = {
  underTabs:
    "Contact your Farmlands representative to discuss your Sales Agreements or start a new one.",
  underAgreementDetails: "Contact your Farmlands representative to discuss this Sales Agreement.",
  noAgreements: "Contact your Farmlands representative.",
  productDetails: "If you have any questions please contact your Farmlands representative.",
};

export enum ScrollYPositionKeys {
  Seeds = "scrollYPositionOnSeeds",
  Quotes = "scrollYPositionOnQuotes",
  Activities = "scrollYPositionOnActivities",
  Orders = "scrollYPositionOnOrders",
  Products = "scrollYPositionOnProducts",
  Agreements = "scrollYPositionOnAgreements",
  AccountSettings = "scrollYPositionOnAccountSettings",
  Statements = "scrollYPositionOnStatements",
  Cards = "scrollYPositionOnCards",
  Payments = "scrollYPositionOnPayments",
}

export const UNIT_MAP = {
  tonne: "t",
  t: "t",
  each: "unit",
  ea: "unit",
  slg: "sl",
  sling: "sl",
  sl: "sl",
  kg: "kg",
  kilo: "kg",
  carton: "ctn",
  ctn: "ctn",
};

export const UNIT_MAP_LONG = {
  bag: "bag",
  binScore: "binScore",
  bkt: "bucket",
  box: "box",
  can: "can",
  case: "case",
  cd: "card",
  cm: "centimetre",
  cm2: "square centimetre",
  cm3: "cubic centimetre",
  ctn: "carton",
  carton: "carton",
  day: "degree brix",
  degC: "degree celsius",
  drum: "drum",
  ea: "unit",
  each: "unit",
  floz: "fluid ounce",
  ft: "foot",
  ft2: "square foot",
  ft3: "cubic foot",
  g: "gram",
  gal: "gallon",
  ha: "hectare",
  hr: "hour",
  in: "inch",
  in2: "square inch",
  in3: "cubic inch",
  ke: "kernel",
  kg: "kilogram",
  kilo: "kilogram",
  km: "kilometre",
  kWh: "kilowatt hour",
  L: "litre",
  lb: "pound",
  ltn: "long ton (UK)",
  m: "metre",
  m2: "square metre",
  m3: "cubic metre",
  mg: "milligram",
  "mg/kg": "milligram per kilogram",
  mi: "mile",
  mi2: "square mile",
  min: "minute",
  mL: "millilitre",
  mm: "millimetre",
  mm2: "square millimetre",
  mm3: "cubic millimetre",
  mo: "month",
  ohm: "ohm",
  oz: "ounce",
  pail: "pail",
  pc: "piece",
  pct: "percent",
  pH: "pH value",
  pkt: "packet",
  plt: "pallet",
  pod: "pod",
  ppm: "parts per million",
  pr: "pair",
  pt: "pint",
  qt: "quart",
  qty: "quantity",
  roll: "roll",
  sack: "sack",
  sec: "second",
  sl: "sling",
  slg: "sling",
  sling: "sling",
  stn: "short ton (US)",
  t: "tonne",
  tonne: "tonne",
  tray: "tray",
  unit: "unit load",
  x: "times",
  yd: "yard",
  yd2: "square yard",
  yd3: "cubic yard",
};

export const UNIT_MAP_PLURAL = {
  tonne: "Tonnes",
  t: "Tonnes",
  each: "Units",
  ea: "Units",
  slg: "Slings",
  sling: "Slings",
  sl: "Slings",
  kg: "Kg",
  kilo: "Kg",
  ctn: "Cartons",
  bag: "Bags",
  binScore: "BinScores",
  bkt: "Buckets",
  box: "Boxes",
  can: "Cans",
  case: "Cases",
  cd: "Cards",
  cm: "Centimetres",
  cm2: "Square Centimetres",
  cm3: "Cubic Centimetres",
  drum: "Drums",
  floz: "Fluid Ounces",
  ft: "Feet",
  ft2: "Square Feet",
  ft3: "Cubic Feet",
  g: "Grams",
  gal: "Gallons",
  ha: "Hectares",
  hr: "Hours",
  in: "Inches",
  in2: "Square Inches",
  in3: "Cubic Inches",
  ke: "Kernels",
  km: "Kilometres",
  kWh: "Kilowatt Hours",
  L: "Litres",
  lb: "Pounds",
  ltn: "Long Tons (UK)",
  m: "Metres",
  m2: "Square Metres",
  m3: "Cubic Metres",
  mg: "Milligrams",
  "mg/kg": "Milligrams per Kilogram",
  mi: "Miles",
  mi2: "Square Miles",
  min: "Minutes",
  mL: "Millilitres",
  mm: "Millimetres",
  mm2: "Square Millimetres",
  mm3: "Cubic Millimetres",
  mo: "Months",
  ohm: "Ohms",
  oz: "Ounces",
  pail: "Pails",
  pc: "Pieces",
  pct: "Percents",
  pH: "pH Values",
  pkt: "Packets",
  plt: "Pallets",
  pod: "Pods",
  ppm: "Parts per Million",
  pr: "Pairs",
  pt: "Pints",
  qt: "Quarts",
  qty: "Quantities",
  roll: "Rolls",
  sack: "Sacks",
  sec: "Seconds",
  stn: "Short Tons (US)",
  tray: "Trays",
  unit: "Unit Loads",
  x: "Times",
  yd: "Yards",
  yd2: "Square Yards",
  yd3: "Cubic Yards",
};

export const pluraliseUnit = (unit: string, quantity: number) => {
  return quantity <= 1
    ? UNIT_MAP_LONG[unit as keyof typeof UNIT_MAP_LONG]
    : UNIT_MAP_PLURAL[unit as keyof typeof UNIT_MAP_PLURAL];
};

export const capitaliseFirstLetter = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};
